import { supabase } from '@/integrations/supabase/client';
import { log } from '@/lib/logger';
import { dedupedRefreshSession } from '@/lib/getRefreshedToken';

export class AuthenticationError extends Error {
  constructor(message: string = 'Authentication required') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

export class SessionExpiredError extends Error {
  constructor(message: string = 'Session expired, please sign in again') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/**
 * Ensures user is authenticated and session is valid
 * Attempts to refresh if session is expired
 * Throws AuthenticationError if not authenticated
 */
export const ensureAuth = async (): Promise<{ userId: string }> => {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      log.error('Auth session error', { error: sessionError, auth_check: 'session_error' });
      throw new SessionExpiredError(sessionError.message);
    }
    
    if (!session) {
      throw new AuthenticationError('No active session');
    }
    
    // Check if session is about to expire (within 5 minutes)
    const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
    const fiveMinutes = 5 * 60 * 1000;
    
    if (expiresAt && Date.now() > expiresAt - fiveMinutes) {
      try {
        await dedupedRefreshSession();
        const { data: { session: refreshed } } = await supabase.auth.getSession();
        if (refreshed) return { userId: refreshed.user.id };
      } catch {
        log.warn('Session refresh failed');
        throw new SessionExpiredError('Session expired and refresh failed');
      }
    }
    
    return { userId: session.user.id };
  } catch (error) {
    if (error instanceof AuthenticationError || error instanceof SessionExpiredError) {
      throw error;
    }
    
    // Network errors (e.g., "Load failed", "Failed to fetch") should NOT become AuthenticationError
    // This prevents redirect loops on flaky mobile networks
    const isNetworkError = error instanceof TypeError && 
      (error.message.includes('Load failed') || error.message.includes('Failed to fetch') || error.message.includes('network'));
    
    if (isNetworkError) {
      log.warn('Network error during auth check', { error });
      // Re-throw as-is so callers can handle gracefully (e.g., show toast, retry)
      throw error;
    }

    log.error('Unexpected auth check error', { error, auth_check: 'unexpected_error' });
    throw new AuthenticationError('Authentication check failed');
  }
};

/**
 * Check if user is authenticated without throwing
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
  } catch {
    return false;
  }
};

/**
 * Get current user ID or null if not authenticated
 */
export const getCurrentUserId = async (): Promise<string | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.id || null;
  } catch {
    return null;
  }
};
